import { useMantineTheme, Group } from "@mantine/core"
import { Card } from "@tfg/ui"
import { useCallback, useEffect, useRef, useState } from "react"

import { GetProductPageComponentsResponseDto } from "~/gen/api"
import {
  getActiveLanguagesList,
  useActiveLanguages,
} from "~/pages/Brand/CommunicationStudio/DesignStudio/hooks/useActiveLanguages"
import {
  createIframeMessages,
  sendMessagesToIframe,
} from "~/pages/Brand/CommunicationStudio/DesignStudio/utils/iframe-utils"
import { useGetAgecData } from "~/pages/Brand/CommunicationStudio/DesignStudio/utils/stubs"
import { useProductSheetGroup } from "~/pages/Brand/CommunicationStudio/contexts/productSheetGroup/useProductSheetGroup"

import { useDesignTokenEditor } from "../contexts/designTokenEditor/useDesignTokenEditor"

type PreviewContainerProps = {
  productPageComponents: GetProductPageComponentsResponseDto[]
}

export const PreviewContainer = ({
  productPageComponents,
}: PreviewContainerProps) => {
  const styleIframeRef = useRef<HTMLIFrameElement>(null)
  const searchEngineIframeRef = useRef<HTMLIFrameElement>(null)
  const { styleData, configurations } = useDesignTokenEditor()
  const theme = useMantineTheme()
  const [isIframeReady, setIsIframeReady] = useState(false)
  const { selectedProductSheetGroup } = useProductSheetGroup()
  const activeLanguages = useActiveLanguages(selectedProductSheetGroup?.id)
  const agecData = useGetAgecData()

  const postToIframes = useCallback(() => {
    const messages = createIframeMessages(
      styleData,
      configurations,
      agecData,
      productPageComponents,
      selectedProductSheetGroup,
      getActiveLanguagesList(activeLanguages)
    )
    messages.styleIframe.forEach((message) =>
      sendMessagesToIframe(styleIframeRef, message)
    )
    messages.searchEngineIframe.forEach((message) =>
      sendMessagesToIframe(searchEngineIframeRef, message)
    )
  }, [
    styleData,
    configurations,
    selectedProductSheetGroup,
    productPageComponents,
    activeLanguages,
    agecData,
  ])

  const handleIframeReady = useCallback((event: MessageEvent) => {
    if (event.data?.type === "IFRAME_READY") {
      setIsIframeReady(true)
    }
  }, [])

  useEffect(() => {
    window.addEventListener("message", handleIframeReady)
    return () => window.removeEventListener("message", handleIframeReady)
  }, [handleIframeReady])

  useEffect(() => {
    if (isIframeReady) {
      postToIframes()
    }
  }, [isIframeReady, postToIframes])

  useEffect(() => {
    window.addEventListener("message", handleIframeReady)
    return () => window.removeEventListener("message", handleIframeReady)
  }, [handleIframeReady])

  useEffect(() => {
    if (isIframeReady) postToIframes()
  }, [isIframeReady, postToIframes])

  return (
    <Group wrap="nowrap" h="100%" w="100%">
      <Card w="100%" miw={theme.other.widths.card.width.lg} p={0} h="100%">
        <iframe
          src="/preview/product-sheet"
          style={{ width: "100%", height: "100%", border: "none" }}
          title="Product Sheet Preview"
          ref={styleIframeRef}
        />
      </Card>
      <Card w="100%" miw={theme.other.widths.card.width.lg} p={0} h="100%">
        <iframe
          src={`/preview/search-engine/${selectedProductSheetGroup?.slug}`}
          style={{ width: "100%", height: "100%", border: "none" }}
          title="Search Engine Preview"
          ref={searchEngineIframeRef}
        />{" "}
      </Card>
    </Group>
  )
}
